import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { fromEvent, merge, Subject, Subscription } from 'rxjs';
import { filter, takeUntil, throttleTime } from 'rxjs/operators';
import { LoginSocketService } from '../../events/chat/login-socket.service';

const THROTTLE_MS = 30 * 1000;

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keypress', 'keydown', 'scroll', 'touchstart', 'wheel'];

@Injectable({
  providedIn: 'root',
})
export class ActivityTrackerService implements OnDestroy {
  private readonly stop$ = new Subject<void>();
  private subscription: Subscription | null = null;
  private active = false;

  constructor(private readonly loginSocket: LoginSocketService, private readonly zone: NgZone) {}

  public start(): void {
    if (this.active) {
      return;
    }
    this.active = true;

    this.zone.runOutsideAngular(() => {
      const documentEvents = ACTIVITY_EVENTS.map((evt) => fromEvent(document, evt, { passive: true }));
      const focusEvent = fromEvent(window, 'focus');
      const visibilityEvent = fromEvent(document, 'visibilitychange').pipe(filter(() => !document.hidden));

      this.subscription = merge(...documentEvents, focusEvent, visibilityEvent)
        .pipe(
          filter(() => document.hasFocus() && !document.hidden),
          throttleTime(THROTTLE_MS, undefined, { leading: true, trailing: false }),
          takeUntil(this.stop$)
        )
        .subscribe(() => {
          this.loginSocket.emitUserActivity();
        });
    });

    this.loginSocket.emitUserActivity();
  }

  public stop(): void {
    if (!this.active) {
      return;
    }
    this.active = false;
    this.stop$.next();
    this.subscription?.unsubscribe();
    this.subscription = null;
  }

  public ngOnDestroy(): void {
    this.stop();
    this.stop$.complete();
  }
}
